<script>
	import { createEventDispatcher } from "svelte";
	import {
		Button,
		Modal,
		ModalBody,
		ModalFooter,
		ModalHeader,
	} from "@sveltestrap/sveltestrap";
	import CustomSearch from "../../../../components/CustomSearch/CustomSearch.svelte";
	import ErrorMessageHandler from "../../../../components/ErrorMessageHandler/ErrorMessageHandler.svelte";
	import {
		addIssuesToWorkOrder,
		createWorkOrder,
		getVehicleWorkOrders,
	} from "src/services/maintenanceRepair";

	export let isOpen = false;
	export let toggle = () => {};
	export let vehicleId = "";
	export let issueIds = [];
	export let issues = [];
	export let flowType = "Workshop";

	const dispatch = createEventDispatcher();

	let destination = "new";
	let selectedWorkOrder = "";
	let error = "";
	let workOrderOptions = [];
	let isSubmitting = false;

	function resetAndToggle() {
		destination = "new";
		selectedWorkOrder = "";
		error = "";
		workOrderOptions = [];
		toggle();
	}

	function handleDismiss() {
		if (isSubmitting) return;
		resetAndToggle();
	}

	// Loaded eagerly (rather than via CustomSearch's loadOptions, which only
	// fires once the user starts typing) so the vehicle's work orders are
	// already listed the moment this field appears.
	$: if (destination === "existing") {
		getVehicleWorkOrders(vehicleId, "").then((items) => {
			workOrderOptions = items;
		});
	}

	async function handleAdd() {
		if (isSubmitting) return;
		if (destination === "existing" && !selectedWorkOrder) {
			error = "Select a work order before adding.";
			return;
		}

		isSubmitting = true;
		error = "";
		try {
			const res =
				destination === "existing"
					? await addIssuesToWorkOrder(selectedWorkOrder.value, {
							issueIds,
							addedVia: "Grouping",
							issues,
						})
					: await createWorkOrder({
							vehicleId: Number(vehicleId),
							issueIds,
							flowType,
							issues,
						});

			if (res.error) {
				// The backend's own message, shown in the modal's banner.
				error =
					res.error.message ||
					"Something went wrong. Please try again.";
				return;
			}

			const workOrderId =
				destination === "existing"
					? res.data.workOrder.id
					: res.data.id;
			dispatch("confirm", {
				destination,
				workOrderId,
				rejected: res.data.rejected ?? [],
			});
			resetAndToggle();
		} finally {
			isSubmitting = false;
		}
	}

	$: isAddDisabled =
		isSubmitting || (destination === "existing" && !selectedWorkOrder);
</script>

<!-- Can't be dismissed (backdrop or Esc) while a request is in flight. -->
<Modal
	{isOpen}
	toggle={handleDismiss}
	backdrop={isSubmitting ? "static" : true}
	keyboard={!isSubmitting}
	centered
>
	<ModalHeader class="add-issues-modal-header"
		>Add issues to work order</ModalHeader
	>
	<ModalBody>
		<ErrorMessageHandler message={error} />
		<p class="wo-destination-question">
			Add issues to new or existing work order?
		</p>
		<div class="wo-destination-options">
			<label class="wo-destination-option">
				<input
					type="radio"
					name="wo-destination"
					value="new"
					bind:group={destination}
				/>
				Create new work order
			</label>
			<label class="wo-destination-option">
				<input
					type="radio"
					name="wo-destination"
					value="existing"
					bind:group={destination}
				/>
				Add to existing work order
			</label>
		</div>
		{#if destination === "existing"}
			<div class="wo-search-field">
				<div class="wo-search-label">Search an existing work order</div>
				<CustomSearch
					label="Search an existing work order"
					placeholder="e.g. WO20260101_A123B"
					containerClasses="search-icon"
					bind:value={selectedWorkOrder}
					items={workOrderOptions}
				/>
			</div>
		{/if}
	</ModalBody>
	<ModalFooter>
		<Button
			class="themeButton backButton px-4"
			disabled={isSubmitting}
			on:click={handleDismiss}
		>
			Cancel
		</Button>
		<Button
			class="themeButton primary px-4"
			disabled={isAddDisabled}
			on:click={handleAdd}
		>
			Add
		</Button>
	</ModalFooter>
</Modal>

<style>
	:global(.add-issues-modal-header .modal-title) {
		font-size: 20px;
		font-weight: 700;
		line-height: 28px;
		color: var(--fds-on-surface-container, #00071f);
	}

	.wo-destination-question {
		margin-bottom: 16px;
		font-size: 16px;
		font-weight: 400;
		line-height: 20px;
		color: var(--fds-on-surface-container, #00071f);
	}

	.wo-destination-options {
		display: flex;
		flex-direction: column;
		gap: 16px;
	}

	.wo-destination-option {
		display: flex;
		align-items: center;
		gap: 8px;
		font-size: 16px;
		font-weight: 400;
		line-height: 20px;
		color: #000;
	}

	.wo-search-label {
		font-size: 16px;
		font-weight: 400;
		line-height: 20px;
		color: var(--fds-on-surface, #00071f);
	}

	.wo-search-field :global(input),
	.wo-search-field :global(input::placeholder) {
		font-size: 16px !important;
		font-weight: 400 !important;
		line-height: 20px;
	}

	.wo-search-field {
		display: flex;
		flex-direction: column;
		gap: 8px;
		margin-top: 32px;
		max-width: 343px;
	}
</style>
